// shared/src/runtime-params.ts
// Runtime-tunable params reader. See docs/runtime-params-2026-05-25-brief-revision.md.
//
// Replaces the compile-time PARAMS object (shared/src/params.ts) with a D1-backed
// runtime_params table read at worker entry. Per-isolate cache, 60s TTL, fail-loud
// Zod parse per feedback_no_fallbacks (no defaults, no silent ignore).
//
// Usage pattern at worker entry (#50 — each worker loads ONLY the keys it declares):
//
//   export default {
//     async scheduled(event, env, ctx) {
//       const params = await loadWorkerParams(env.DB, ['CONTEXT_TICKERS', 'DUNE_HOST'] as const);
//       for (const ticker of params.CONTEXT_TICKERS) { ... }
//     }
//   };
//
// Then thread `params` through pure-function call chains. Pure functions never call
// loadWorkerParams() themselves — entry points do the one read per invocation; the per-key-set
// cache amortizes across invocations within the same isolate within 60 seconds. Reading a key
// the worker didn't declare is a COMPILE error (tsc), so a missing key can't reach runtime, and a
// param a worker doesn't declare can't break it — that is what removed the fleet redeploy tax.
// (The old full-strict loadRuntimeParams() all-keys loader was removed 2026-07-27 — see below.)

import { z } from 'zod';
import { CronScheduleParamSchema } from './cron-schedule.js';
import { EconCalendarParamSchema } from './econ-calendar-param.js';

// ─── Schema ─────────────────────────────────────────────────────────────────
// Seed source: migrations/0017_runtime_params.sql (33 rows); +AIG_BYOK_ALIAS
// (0035) +9 keys (0036: AIG_ACCOUNT_ID, AIG_GATEWAY_SLUG, LLM_TIMEOUT_MS,
// COINGECKO_HOST, FRED_HOST, FINNHUB_HOST, POLYGON_HOST, DUNE_HOST,
// AMM_DUNE_QUERY_ID) = 43; +2 keys (0037: COST_SOFT_WARN_USD,
// COST_HARD_ABORT_USD) = 45; +1 key (0040: ACCOUNT_ADDRESSES) = 46;
// +3 keys (0041: ACCOUNT_CHAINS, ACCOUNT_BASELINE_DATE, ACCOUNT_DUST_FLOOR_USD)
// = 49; +1 key (0043: ACCOUNT_SPAM_CONTRACTS) = 50; +1 key (0045: CRON_SCHEDULE)
// = 51; +2 keys (0047: DUNE_POLL_INTERVAL_MS, DUNE_MAX_POLL_ATTEMPTS) = 53;
// +23 keys (0048: retention/DR 7 + vendor-timeout/pacing 6 + misc knobs 10)
// = 77 keys total (NOTE: the historical "76" in 0048's comment + memory
// undercounted by 1 — CRON_SCHEDULE uses CronScheduleParamSchema, not z.*, so a
// z.-field grep misses it). 0051 briefly added ACCOUNT_LIVE_CACHE_TTL_S (→78)
// but it was removed again (migration 0052) when the live design was reverted
// — back to 77. +1 key (0056: ACCOUNT_BASELINE_LP_FREE_SCAN_DAYS) = 78 total —
// the LP-free baseline-scan window for account-worker reconcileAccount (lp-pools
// baseline+fee recompute, 2026-06-08). +1 key (0058: ACCOUNT_CHAIN_RECHECK_HOURS)
// = 79 total — the skip-empty-chains full-rescan cadence for account-worker
// (lp-pools value-history follow-up, 2026-06-09). +1 key (0059: ECON_CALENDAR)
// = 80 total — the operator-curated econ-calendar source (JSON array of upcoming
// US macro events) UNION+DEDUP'd with the FRED calendar at api-worker
// /api/econ-calendar (manual-wins), so the operator can fix bad FRED dates (the
// FOMC-on-6/10 bug) + add events FRED's free feed omits. See
// docs/econ-calendar-param-2026-06-11-brief-revision.md + ./econ-calendar-param.ts.
// +1 key (0062: REGIME_PIVOT_GUARDRAIL_ATR_MULT) = 81 total — the ATR-multiple
// leash for the p.regime channel-line guardrail clamp (2026-06-18). See
// docs/regime-channel-2026-06-18-brief-revision.md.
// (Tally above stops at 0062; 0063 PAX +5, 0067 +1, 0069 +1 → 88 by 2026-06-27.)
// +1 key (0070: REGIME_TREND_LOOKBACK_BARS) = 89 total — the daily-close lookback
// for the p.regime TREND-STRUCTURE block (2026-06-30 trend-read redesign).
// +1 key (0074: MACRO_NEWS_QUERY) = 91 total (the SEED_ROWS test asserted 90
// before this — the comment tally has long lagged the test) — the tunable GDELT
// query for the chain macro-news pull (theme-based; 2026-07-08 news-coverage
// fix). Then 2 regime keys (REGIME_PIVOT_GUARDRAIL_ATR_MULT + REGIME_TREND_LOOKBACK_BARS)
// were removed with the p.regime retirement (2026-07-09) → 89. That hand-tally
// is HISTORICAL and no longer maintained — later keys (LLM_STEP_MODEL_OVERRIDE
// 0080; +6 retention #101/0108; ORACLE_OBS_FULLRES_DAYS #107/0109) took it past 89.
// Trust the runtime-params.test.ts SEED_ROWS toHaveLength() assertion, not this tally.
// .strict() rejects unexpected D1 keys per feedback_no_fallbacks — adding a new
// key requires a deploy with this schema extended; the operational workflow is
// "schema update + deploy first, then INSERT D1 row".

const PARAM_SHAPE = z.object({
  // ── Tickers / portfolio (3) ──
  // 2026-07-02 union model (docs/target-context-union-2026-07-02-brief-revision.md):
  // the effective universe every consumer prices/contexts/allowlists over is
  // `effectiveUniverse(params)` = CONTEXT_TICKERS ∪ TARGET_TICKERS (see the
  // helper below). TARGET is therefore a subset of the universe BY CONSTRUCTION
  // — an operator adds a signal ticker by editing TARGET_TICKERS alone; it is
  // auto-priced + auto-trusted + auto-rendered without also touching CONTEXT.
  //
  // CONTEXT_TICKERS now means "context-only extras": tickers that get a LEAN
  // analyst pass + serve as cluster_peer / cross-asset context, and are the
  // trusted-allowlist base for the macro_predictions writer (tickers in the
  // effective universe skip the vendor-probe step in validateAndExtendUniverse
  // — Session 5R migration 0027 consolidation dropped the parallel
  // MACRO_PREDICTION_UNIVERSE_SEED param). CONTEXT need NOT list the targets;
  // the union folds them in.
  //
  // TARGET_TICKERS is the set that ALSO gets the expensive DEEP arbiter pass
  // and produces signals table rows + dashboard PORTFOLIO TICKERS render.
  // Universe-only tickers (in the effective universe but not in TARGET) flow as
  // cross-ticker context only — no signal row, no dashboard prediction render.
  //
  // Names rationale (Session 5R 2026-05-27 rename): the old TICKERS /
  // PORTFOLIO_TICKERS pair was ambiguous (TICKERS sounded like "what I care
  // about", but was the wider universe). CONTEXT/TARGET makes the roles
  // mechanically obvious and reads naturally on the Operations Params tab.
  // Migrations 0023 (added TARGET as subset) + 0024 (rename) + 0026 (CONTEXT
  // restated as independent market universe) + 0027 (drop macro seed) are
  // the writer side of this Zod contract.
  CONTEXT_TICKERS:                z.array(z.string().min(1)).min(1),
  TARGET_TICKERS:                 z.array(z.string().min(1)).min(1),
  TICKER_CLUSTERS:                z.record(z.string(), z.array(z.string().min(1))),

  // ── RSS (10) — OPERATIONALLY RETIRED (#120, 2026-07-21) ──
  // The RSS feed lists moved to the oracle_feeds_config D1 table (migration 0113),
  // editable from the /oracle POLICY & SETTINGS tab with no deploy. Both live readers
  // now read that table, not these params: the refresh digest (fetchRssAllGroups, Slice 1)
  // and the news_rss LLM tool (fetchNewsRss, Slice 3 — by feed_group). ZERO production
  // src/** readers remain. Made .optional() (mirrors MACRO_NEWS_QUERY / #73) and the ten
  // D1 rows deleted, so no worker requires them and no live consumer exists. The keys +
  // fixtures are intentionally left for one more fleet deploy; the full schema/fixture
  // removal is batched onto the next shared/src fleet deploy (DEFERRED_BACKLOG D-106) to
  // avoid a second dedicated ~17-worker redeploy.
  RSS_CRYPTO_FEEDS:  z.string().min(1).optional(),
  RSS_CRYPTO_MAX:    z.number().int().positive().optional(),
  RSS_WEATHER_FEEDS: z.string().min(1).optional(),
  RSS_WEATHER_MAX:   z.number().int().positive().optional(),
  RSS_CB_FEEDS:      z.string().min(1).optional(),
  RSS_CB_MAX:        z.number().int().positive().optional(),
  RSS_RATES_FEEDS:   z.string().min(1).optional(),
  RSS_RATES_MAX:     z.number().int().positive().optional(),
  RSS_NEWS_FEEDS:    z.string().min(1).optional(),
  RSS_NEWS_MAX:      z.number().int().positive().optional(),

  // ── Polymarket (7 — 6 static + 1 JSON record map) ──
  POLY_SIGNAL_ENABLED: z.enum(['YES', 'NO']),
  POLY_SIGNAL_TICKERS: z.string().min(1),
  POLY_MIN_VOLUME:     z.number().nonnegative(),
  POLY_MAX_DAYS:       z.number().int().positive(),
  POLY_MAX_MARKETS:    z.number().int().positive(),
  POLY_FETCH_LIMIT:    z.number().int().positive(),
  POLY_QUERIES:        z.record(z.string(), z.string().min(1)),

  // ── AMM (2) ──
  AMM_TARGET_POOL_ADDRESSES:  z.string().min(1),
  AMM_BENCHMARK_POOL_ADDRESS: z.string().min(1),

  // ── Account (7) ──
  // ACCOUNT_ADDRESSES parameterized 2026-06-04 (migration 0040). The tracked
  // account was a hardcoded `REVERT_ACCOUNT = 'lightdex.eth'` constant in
  // amm-worker; this single key now drives BOTH the existing LP-positions
  // section (Revert, via amm-worker which reads ACCOUNT_ADDRESSES[0]) AND the
  // new Account Holdings & Value section (Alchemy, via account-worker which
  // values every entry). Entries may be ENS names ('name.eth') or raw 0x
  // addresses — resolveEns() passes a bare address through unchanged. .min(1)
  // guarantees [0] exists.
  ACCOUNT_ADDRESSES:          z.array(z.string().min(1)).min(1),
  // The 3 feature params below landed at migration 0041 (C5) — Account Holdings
  // & Value pipeline. account-worker reads them at fire and threads CHAINS +
  // DUST_FLOOR + the baseline-derived sinceIso into account-fetcher's RPC args.
  // ACCOUNT_CHAINS: chain-ids to scan (subset of 1/137/42161/10/8453).
  ACCOUNT_CHAINS:             z.array(z.string().min(1)).min(1),
  // ACCOUNT_BASELINE_DATE: default comparison day (YYYY-MM-DD) + flows-window
  // floor. Read-time deltas in api-worker; UI date-picker can override.
  ACCOUNT_BASELINE_DATE:      z.string().min(1),
  // ACCOUNT_DUST_FLOOR_USD: drop wallet tokens valued below this USD floor.
  ACCOUNT_DUST_FLOOR_USD:     z.number().nonnegative(),
  // ACCOUNT_SPAM_CONTRACTS: operator-curated blocklist of ERC-20 contract
  // addresses to drop entirely (airdropped scam tokens — esp. the rare ones
  // that carry a FAKE non-zero DEX price the dust/non-zero filters can't catch).
  // Flat global list (lowercased 0x), matched on ANY chain. Applied write-time
  // in account-fetcher projectBalances, beside the dust floor (migration 0043,
  // C11). Empty array = nothing blocked (default). Editable on the Operations
  // Params tab; an edit takes effect on the next account-worker cron fire.
  ACCOUNT_SPAM_CONTRACTS:     z.array(z.string().min(1)),
  // ACCOUNT_BASELINE_LP_FREE_SCAN_DAYS (migration 0056, 2026-06-08): the ±N-day
  // window account-worker reconcileAccount() scans around ACCOUNT_BASELINE_DATE
  // when synthesizing a new account's value baseline, looking for an LP-FREE day
  // (no active Uniswap-v3 position-manager NFT at that block) so the baseline is
  // clean wallet value, not wallet+LP. If no LP-free day exists in ±N days it
  // falls back to the exact baseline_date and labels the baseline with a loud
  // "had an active LP" caveat. 0 = no scan (always the exact baseline_date). The
  // cap (30) bounds the per-account archival-probe subrequest count under the
  // Workers-Free 50-cap. Consumed ONLY by account-worker, but lives in the shared
  // strict schema so every loadRuntimeParams worker requires the row (deploy-then-
  // migrate). See docs/lp-pools-baseline-and-fees-recompute-2026-06-08.
  ACCOUNT_BASELINE_LP_FREE_SCAN_DAYS: z.number().int().min(0).max(30),
  // ACCOUNT_CHAIN_RECHECK_HOURS (migration 0058, 2026-06-09): account-worker
  // scans only the chains an account held nonzero value on in the LAST snapshot
  // (skip-empty), to cut Alchemy subrequest + token-price-quota pressure. To
  // still catch an account newly funding a chain it had nothing on, EVERY account
  // is FULL-rescanned (all ACCOUNT_CHAINS) when its last full scan is older than
  // this many hours (or it has no prior data). Lower = faster discovery of new
  // chains but more quota use; higher = leaner but slower to notice a new chain.
  // 0 disables skip-empty (always full-scan). Consumed ONLY by account-worker but
  // lives in the shared strict schema so every loadRuntimeParams worker requires
  // the row (deploy-then-migrate). See lp-pools-value-history-backfill-2026-06-08.
  ACCOUNT_CHAIN_RECHECK_HOURS: z.number().int().min(0),

  // ── Bitcoin holdings (1) ── (migration 0069, 2026-06-27)
  // BITCOIN_ADDRESSES — native BTC addresses (bech32 `bc1…` / base58 `1…`/`3…`)
  // tracked on /crypto, SEPARATE from the EVM ACCOUNT_ADDRESSES (different format,
  // provider, validation). Balances come from Dune (Execute SQL: received outputs −
  // spent inputs), valued in USD via CoinGecko — see amm-fetcher fetchBitcoinBalances.
  // Mirrors ACCOUNT_ADDRESSES' lenient shape: any non-empty string per entry;
  // BTC-format validation lives at the boundaries (picker + isBitcoinAddress in the
  // fetcher), NOT in the strict schema, so a malformed manual edit can't brick the
  // fleet's param load. EMPTY ARRAY = feature dormant (no BTC section renders) — the
  // seed default, since this is opt-in (hence NO .min(1)). Consumed by account-worker
  // (fires the BTC snapshot on its cron) + the /crypto BITCOIN picker. Lives in the
  // shared strict schema so every loadRuntimeParams worker requires the row
  // (deploy-then-migrate, 0069). See docs/bitcoin-wallet-monitoring-2026-06-27.
  BITCOIN_ADDRESSES:          z.array(z.string().min(1)),

  // ── DeepSeek / inductive (4) ──
  DEEPSEEK_REASONING_EFFORT: z.enum(['low', 'medium', 'high']),

  // #45 Phase B (2026-07-10) — per-step model override map. Maps a step id
  // (registry STEP, e.g. 'm.synthesis') to a canonical DeepSeek model, letting an
  // operator flip ONE step's model from the Params tab with NO deploy — for
  // next-run-only experiments. Empty {} = every step uses its prompt-module MODEL
  // const (the default). LOOSE AT THE FLEET BOUNDARY like NOTIFICATION_THRESHOLDS:
  // an OPAQUE step→model map, so ADDING a step to the map never re-triggers the
  // fleet fixture lockstep. `.default({})` keeps it optional-on-input (a fixture
  // that omits it still parses). VALUES are enum-validated (a garbage model fails
  // loud). NEVER applied mid-brief: params load once per run, so an in-flight
  // brief keeps the model it started with. The design lock still governs durable
  // config (an override deviating from the lock without an allowlist entry renders
  // a loud un-sanctioned flag on the registry tab; promotion still needs a Brief
  // Revision + allowlist edit). See docs/llm-step-registry-2026-07-10-design.md §B.
  LLM_STEP_MODEL_OVERRIDE: z.record(z.enum(['deepseek-v4-pro', 'deepseek-v4-flash'])).default({}),
  INDUCTIVE_HISTORY_FLOOR_N: z.number().int().positive(),
  // Per-ticker inductive forecaster floor (p.inductive stage). Twin of
  // INDUCTIVE_HISTORY_FLOOR_N but per-ticker — the stage skips a ticker until it
  // has this many prior signals. Seeded by migration 0049 (value 30). Brief rev
  // docs/per-ticker-inductive-forecaster-2026-06-06-brief-revision.md.
  P_INDUCTIVE_HISTORY_FLOOR_N: z.number().int().positive(),
  // AI Gateway BYOK stored-key alias (cf-aig-byok-alias header). Runtime-tunable
  // so a DeepSeek key rotation is a D1 update (or Operations Params tab edit),
  // never a code change + redeploy. Seeded by migration 0035. Consumers: the
  // AI-gateway transport in chain-workflow, p-stage-worker, qa-worker, and
  // macro-prediction-scorer (read at the call site; 60s isolate cache amortizes).
  AIG_BYOK_ALIAS:            z.string().min(1),

  // ── AI Gateway infra identity + transport (3) ──
  // Parameterized 2026-05-31 (migration 0036) — same drift class as the BYOK
  // alias: account id + gateway slug were hardcoded byte-identically in 4 worker
  // transports (chain-workflow, p-stage-worker, qa-worker, macro-prediction-scorer)
  // and the 240s LLM wrapper timeout in 3 of them (with contradictory history
  // comments). Account/slug rotate on a DR rebuild / gateway recreate; the timeout
  // is a genuine latency-tuning knob. Consumers read these at the call site
  // alongside AIG_BYOK_ALIAS (60s isolate cache amortizes). NOTE:
  // macro-prediction-scorer/crux-llm.ts is model-conditional (S-18): Flash calls
  // use its own 60_000 const, but reasoner-class calls (lht.thesis, Pro) DO read
  // LLM_TIMEOUT_MS so a slow reasoning call isn't aborted at 60s.
  AIG_ACCOUNT_ID:            z.string().min(1),
  AIG_GATEWAY_SLUG:          z.string().min(1),
  LLM_TIMEOUT_MS:            z.number().int().positive(),

  // ── Vendor API hosts (5 — pure origins; version + path stay in code) ──
  // Parameterized 2026-05-31 (migration 0036). These were duplicated byte-
  // identically across workers (CoinGecko in 3 files/3 workers; FRED/Finnhub/
  // Polygon across refresh-fetcher; Dune in amm-fetcher). Stored as the ORIGIN
  // only (https://host) — consumers compose the stable version prefix + path in
  // code (e.g. `${COINGECKO_HOST}/api/v3/...`, `${FRED_HOST}/fred/...`). Hosts
  // rotate rarely; D1-editable so a vendor host change is a Params-tab edit, not
  // a multi-file edit + redeploy. AMM Dune saved-query id (separate from the
  // host) is AMM_DUNE_QUERY_ID above.
  COINGECKO_HOST:            z.string().url(),
  FRED_HOST:                 z.string().url(),
  FINNHUB_HOST:              z.string().url(),
  POLYGON_HOST:              z.string().url(),
  DUNE_HOST:                 z.string().url(),

  // ── AMM Dune saved-query id (1) ──
  // Parameterized 2026-05-31 (migration 0036) — sibling of the already-param'd
  // AMM_TARGET_POOL_ADDRESSES / AMM_BENCHMARK_POOL_ADDRESS. The saved-query id
  // has been re-created once (6656001 → 7564956); a future re-create is now a D1
  // update. Consumer: amm-fetcher fetchPoolScoresGroup → executeQueryAndWait.
  AMM_DUNE_QUERY_ID:         z.string().min(1),

  // ── AMM Dune execution poll budget (2) ── (migration 0047, 2026-06-06)
  // amm-fetcher executeQueryAndWait polls Dune /status up to MAX_ATTEMPTS times
  // every INTERVAL_MS until QUERY_STATE_COMPLETED. Deadline = INTERVAL_MS ×
  // MAX_ATTEMPTS. Made param-driven so a slow/cold Dune execution (the recurring
  // `poll timeout … QUERY_STATE_EXECUTING` fail-CLOSED class — bumped 30→45 in
  // May, then param'd here) is tunable from the Params tab with NO redeploy.
  // HARD CAP: fetchPoolScoresGroup's ONLY subrequests are 1 execute + N polls +
  // 1 results, and the Workers-Free cap is 50 → MAX_ATTEMPTS ≤ 47 (1+47+1=49).
  // Zod enforces the ceiling so a Params edit can't silently bust the cap.
  // Consumer: amm-fetcher fetchPoolScoresGroup → executeQueryAndWait opts.
  DUNE_POLL_INTERVAL_MS:     z.number().int().min(1000).max(8000),
  DUNE_MAX_POLL_ATTEMPTS:    z.number().int().min(10).max(47),

  // ── Calibration / scoring (9) ──
  SCORE_OUTCOME_THRESHOLD_PCT: z.number().positive(),
  SCORE_OUTCOME_MAX_ROWS:      z.number().int().positive(),
  ANCHORING_COSINE_MAX:        z.number().min(0).max(1),
  ANCHORING_TICKER_DROP_PCT:   z.number().positive(),
  ANCHORING_CONF_DROP_PCT:     z.number().positive(),
  ANCHORING_BLANK_RATE_MULT:   z.number().positive(),
  PACING_AHEAD_RATIO:          z.number().positive(),
  PACING_BEHIND_RATIO:         z.number().positive(),
  NO_SIGNAL_THRESHOLD_PCT:     z.number().nonnegative(),

  // ── Cost guard (2) ──
  // Promoted to runtime_params 2026-06-01 (migration 0037), split into a soft
  // (warn-only) + hard (abort) pair. The chain-workflow + p-stage-worker
  // cost-guards ENFORCE these — tunable without a redeploy. A brief that runs
  // slightly heavy (normal ~$0.13–0.16) used to be KILLED at the old single
  // $0.15 ceiling AFTER the spend (wasting the run); now it only WARNs at SOFT
  // and aborts only at a true-runaway HARD. NOTE: the `COST_CEILING_USD`
  // constant (shared/src/constants.ts) is INTENTIONALLY retained as the
  // DISPLAY/target budget reference (Ops cost gauge denominator + ZoneCostTrend
  // reference line) — a different concept from these enforcement thresholds.
  COST_SOFT_WARN_USD:          z.number().positive(),
  COST_HARD_ABORT_USD:         z.number().positive(),

  // ── Scheduling (2) ──
  // CRON_SCHEDULE (0045, 2026-06-05) — the entire cron firing schedule, moved
  // out of the hardcoded `SCHEDULE` const into a tunable D1 param so timing
  // changes need no code edit / redeploy. TZ-aware recurrence rules (interval
  // or time-of-day-in-IANA-tz); DST handled automatically. Consumed by the
  // cron-dispatcher (matchSchedule per minute) + api-worker (/api/ops/schedule
  // projection). Refinements (valid tz; BACKUP+REFRESH present) make a bad edit
  // unfireable AT WRITE time. See docs/cron-schedule-parameterization-2026-06-05.
  CRON_SCHEDULE:               CronScheduleParamSchema,

  // SCHEDULED_RUNS_ENABLED (migration 0144, 2026-09-21) — master kill switch for
  // the cron-dispatcher's scheduled fan-out ONLY. `false` makes handleScheduled
  // short-circuit right after computing this tick's matches (no RPC dispatch,
  // no runs row, no check_log) — every OTHER per-tick responsibility
  // (stuck-brief reaper, AMM stale-refire self-heal, D1-capacity + dark-producer
  // alarms, chain-runs sweeper) still runs, since those aren't "a scheduled
  // run" in the operator's sense, they're always-on housekeeping. Manual fires
  // (admin-rerun / POST /api/runs/:id/rerun) dispatch straight to the target
  // worker's RPC and never read this key, so they are UNAFFECTED when the
  // switch is off — that split is the whole point of the toggle. `.default(true)`
  // (fail-open if the D1 row is ever missing, e.g. pre-migration or during the
  // deploy→migrate window) mirrors DIAGNOSTIC_LOG_ENABLED just above; unlike
  // that key, this one DOES get a migration-seeded row (0144) because it must
  // be editable from the Params tab, which requires an existing row (PATCH is
  // UPDATE-only, no upsert). Consumer: workers/cron-dispatcher/src/scheduled.ts.
  SCHEDULED_RUNS_ENABLED:      z.boolean().default(true),

  // ── Retention / D1-size & DR policy (16) ── (migration 0048, 2026-06-06; +3 migration 0079, 2026-07-10; +6 #101 2026-07-18)
  // Day/week counts for the retention purges. The D1 500MB free-cap is an
  // ACTIVE constraint (the raw-strip cut the DB 440→138MB); these let an
  // operator tune row life from the Params tab with NO redeploy. C018 NOTE:
  // every consumer composes the integer into
  // strftime('%Y-%m-%dT%H:%M:%fZ','now','-N days') — the param IS the integer N
  // only, never a bare datetime() form (the C018 gate stays satisfied).
  SNAPSHOT_RETENTION_DAYS:         z.number().int().positive(),   // refresh_snapshots DELETE life (chain-workflow; amm decoupled to AMM_SNAPSHOT_RETENTION_DAYS since #44)
  AMM_SNAPSHOT_RETENTION_DAYS:     z.number().int().positive(),   // #43: amm_snapshots + amm_liquidity_snapshots DELETE life (chain-workflow) AND the /api/amm positions-history chart window cap (api-worker clampDaysToRetention). Was an in-code const (90); promoted to a runtime_param seeded at 45 for the D1 500MB capacity fit.
  RAW_BLOB_STRIP_DAYS:             z.number().int().positive(),   // raw-vendor-blob json_remove window (chain-workflow)
  CHECK_LOG_PASS_RETENTION_DAYS:   z.number().int().positive(),   // PASS-severity check_log purge (chain-workflow)
  LLM_CALLS_RETENTION_DAYS:        z.number().int().positive(),   // S-06 (0079): llm_calls DELETE life — PAX-export-gated (purge only rows covered by a successful export_runs) (chain-workflow)
  RUNS_RETENTION_DAYS:             z.number().int().positive(),   // S-37 (0079): runs registry terminal-status DELETE life; in-flight 'running' rows never purged (chain-workflow)
  CHECK_LOG_SKIP_RETENTION_DAYS:   z.number().int().positive(),   // S-37 (0079): SKIP-severity check_log purge; WARN/FAIL kept indefinitely (chain-workflow)
  ACCOUNT_RAW_RETENTION_DAYS:      z.number().int().positive(),   // account_snapshots full-cadence window
  ACCOUNT_MAX_HISTORY_DAYS:        z.number().int().positive(),   // account_snapshots hard history ceiling
  BACKUP_DAILY_RETENTION_DAYS:     z.number().int().positive(),   // backup-worker GFS daily tier
  BACKUP_WEEKLY_RETENTION_WEEKS:   z.number().int().positive(),   // backup-worker GFS weekly tier
  // #101 (2026-07-18): promoted from in-code consts now that #50's loadWorkerParams
  // removed the fleet-redeploy/fixture tax — operator-tunable retention with no deploy.
  NEWS_ITEMS_RETENTION_DAYS:          z.number().int().positive(), // news_items DELETE life (chain-workflow retention purge)
  NEWS_PROCESSING_LOG_RETENTION_DAYS: z.number().int().positive(), // news_processing_log (dedup/tier audit) DELETE life (chain-workflow)
  ORACLE_OBS_RETENTION_DAYS:          z.number().int().positive(), // oracle_observations DELETE life (oracle-worker, narrow-SELECT read)
  ORACLE_OBS_FULLRES_DAYS:            z.number().int().positive().optional(), // #107: oracle_observations kept at full 5-min resolution for N days; older rows downsampled to 1/hour per (key,source) by the purge (oracle-worker reads it via a narrow SELECT with its OWN fail-loud, NOT this schema). .optional() ONLY so the full-strict loadRuntimeParams full-loaders (daily-close invariant + api-worker admin CRUD) tolerate a missing row during the deploy→migrate window — the actual consumer still requires it.
  ORACLE_CANON_RETENTION_DAYS:        z.number().int().positive(), // oracle_canonical DELETE life (oracle-worker)
  ORACLE_RELATIONS_RETENTION_DAYS:    z.number().int().positive(), // oracle_token_relations time-series DELETE life (oracle-worker)
  ORACLE_MACRO_BLOCK_RETENTION_DAYS:  z.number().int().positive(), // oracle_macro_block hourly-blob DELETE life (oracle-worker)

  // ── retention-gap Track 1 (4 previously-unpurged growers) (migration 0141) ── operator-approved
  // windows, 4 INDEPENDENT guarded() DELETEs (chain-workflow) — never a briefs-rooted cascade (D1
  // runtime FK enforcement was left [UNVERIFIED] and market_emit has no FK at all). See
  // docs/investigations/retention-gap-4-tables-and-account-max-history-drift.md Track 1.
  BRIEF_PAYLOADS_RETENTION_DAYS:       z.number().int().positive(), // brief_payloads DELETE life (chain-workflow); unconditional age, brief archive depth
  MARKET_EMIT_RETENTION_DAYS:          z.number().int().positive(), // market_emit DELETE life (chain-workflow); unconditional age. HARD FLOOR: never below ~18d — m.inductive.forecast.ts skips the whole LLM stage below INDUCTIVE_HISTORY_FLOOR_N (30) prior synthesis rows
  MACRO_PREDICTIONS_RETENTION_DAYS:    z.number().int().positive(), // macro_predictions DELETE life (chain-workflow); age AND already-scored (EXISTS a macro_prediction_outcomes row via its observables) — an in-flight/unscored prediction is NEVER purged regardless of age
  MACRO_PREDICTION_DRIFT_DAILY_RETENTION_DAYS: z.number().int().positive(), // macro_prediction_drift_daily DELETE life (chain-workflow); unconditional age on `date` (no created_at column — C018-SAFE date-form cutoff, not a strftime ISO-Z one)

  // ── News diagnostic-log kill switch (1) ── (cf#110, 2026-09-04)
  // `news_processing_log` (dedup/tier/tone decisions) is threshold-tuning OBSERVABILITY
  // ONLY — shared/src/news/write-log.ts:10-12 documents that nothing branches on it — yet
  // its writes were 8.6% of the account's 100k-row/day D1 write cap
  // (docs/audits/2026-09-04-d1-rows-written-overnight-spike.md §5). This is the enable
  // gate: `false` makes writeNewsProcessingLog() a no-op (zero db.batch calls) regardless
  // of `rows`; `true` (or the row simply not existing yet, e.g. pre-migration) preserves
  // today's always-write behaviour via `.default(true)` — an EXPLICIT schema default, not
  // a swallowed read error, so a genuinely wrong-typed value (e.g. the string "off") still
  // fail-louds instead of silently defaulting. Threaded to the writer as a plain boolean
  // (mirrors the effectiveUniverse()/Pick<RuntimeParams,...> pure-function pattern below) —
  // the worker-side call sites that must pass `params.DIAGNOSTIC_LOG_ENABLED` in are a
  // sibling Cut's territory (workers/), tracked separately from this shared/-only Cut.
  DIAGNOSTIC_LOG_ENABLED: z.boolean().default(true),

  // ── Vendor fetch timeout + rate-limit pacing (6) ── (migration 0048)
  // FRED 429 storms + Polygon Free 5-req/min were multi-incident battles; these
  // are the live-retune knobs. VENDOR_FETCH_TIMEOUT_MS + NEWS_FETCH_TIMEOUT_MS
  // each consolidate previously-divergent hardcoded copies (C026 drift fix).
  VENDOR_FETCH_TIMEOUT_MS:         z.number().int().positive(),   // amm-fetcher + account-fetcher outbound fetch abort (was 30_000 ×2)
  NEWS_FETCH_TIMEOUT_MS:           z.number().int().positive(),   // GDELT/Polygon news per-attempt abort (chain 8000 + refresh 6000 ×2)
  STOCKS_PER_TICK:                 z.number().int().positive(),   // refresh-fetcher stocks/tick (Polygon 5/min + CF-50 cap)
  POLYGON_MIN_GAP_MS:              z.number().int().nonnegative(),// daily-close-worker Polygon throttle gap
  FRED_FRESH_HOURS:                z.number().int().positive(),   // refresh-fetcher FRED fresh TTL (hours)
  FRED_COOLDOWN_HOURS:             z.number().int().positive(),   // refresh-fetcher FRED 429 cooldown (hours)

  // ── Misc operator knobs (10) ── (migration 0048)
  LLM_STEP_RETRY_LIMIT:            z.number().int().nonnegative(),// chain + p-stage llm step.do retry count
  LLM_STEP_RETRY_DELAY_SEC:        z.number().int().positive(),   // chain + p-stage llm step.do retry delay (s)
  NEWS_LOOKBACK_HOURS:             z.number().int().positive(),   // p-stage news recency window (hours)
  NEWS_MAX_ITEMS:                  z.number().int().positive(),   // p-stage news per-ticker item cap
  NEWS_GDELT_RECORDS:              z.number().int().positive(),   // p-stage GDELT maxrecords
  // (MACRO_NEWS_QUERY (migration 0074) fully retired D-104/cf#6, 2026-08-19: #68 re-sourced the
  //  macro-news producer GDELT→Finnhub so the param was orphaned; key + D1 row + all fixtures removed.)
  ACCOUNT_FLOWS_MAX_PAGES:         z.number().int().positive(),   // account-fetcher transfer pages /(addr×chain×dir)
  CRUX_LLM_FAN_OUT_CAP:            z.number().int().positive(),   // macro-prediction-scorer crux LLM briefs/fire
  QA_FRESH_CONTEXT_TIMEOUT_MS:     z.number().int().positive(),   // qa-worker fresh-context build budget (ms)
  BRIEF_CACHE_TTL_SECONDS:         z.number().int().positive(),   // api-worker BRIEF_CACHE KV expirationTtl (s)

  // ── Econ calendar (1) ── (migration 0059, 2026-06-11)
  // Operator-curated upcoming US macro events (JSON array). UNION+DEDUP'd with
  // the FRED-sourced calendar at api-worker /api/econ-calendar (manual-wins),
  // so the operator can fix bad FRED dates (the trigger: FRED's "FOMC Press
  // Release" forward-dated the Fed decision to 6/10; real meeting 6/17) and add
  // events FRED's free release-dates feed omits. Empty array = FRED-only.
  // Edit on the Operations Params tab; a change reflects on the NEXT
  // /api/econ-calendar request (merge runs after the KV read, not after TTL).
  // Schema lives in ./econ-calendar-param.ts (lenient inner shape on purpose —
  // a stray operator field must not throw loadRuntimeParams fleet-wide).
  ECON_CALENDAR:                   EconCalendarParamSchema,

  // ── Ops Notices thresholds (1) ── (migration 0073, 2026-07-05; consolidates the
  // 0072 ECON_CALENDAR_NOTIFY_* scalars). ONE json param holding EVERY Ops Notices
  // rule threshold so the Params tab stays compact — a single row rendered as a
  // labeled mini-form editor instead of a scalar row per knob.
  //
  // LOOSE AT THE FLEET BOUNDARY (decided 2026-07-05): this key is consumed ONLY by
  // api-worker, so the fleet-wide schema treats it as an OPAQUE number-map. The
  // typed shape + per-field defaults + save-time validation live in
  // shared/src/notifications/thresholds.ts and are applied by api-worker
  // (resolveNotificationThresholds on read; NotificationThresholdsJsonSchema on the
  // admin PATCH). That keeps FUTURE notification-knob additions api-worker-only —
  // no fleet fixture lockstep, no deploy-then-migrate sweep (adding a map entry
  // never changes this line). A newly-added knob uses its code default until the
  // operator sets it. See docs/ops-notices-2026-07-05-design.md.
  NOTIFICATION_THRESHOLDS: z.record(z.number()),

  // ── PAX durable export (5) ── (migration 0063, 2026-06-19)
  // export-worker knobs. WINDOW = daily-delta slice width (days). PART budgets
  // bound each workflow step's CPU (raw bytes) + subrequests (upload cap), reused
  // from the backup dump tuning. MAX_BATCHES bounds chaining per instance.
  // RETENTION = days to keep export bundles + ledger shards on Drive (Drive is a
  // transport buffer; level0x0 is the permanent store). See
  // docs/durable-export-2026-06-19-design.md §6.1/§8.
  EXPORT_DAILY_WINDOW_DAYS:        z.number().int().positive(),   // daily-delta window width (days)
  EXPORT_PART_RAW_BUDGET_BYTES:    z.number().int().positive(),   // per-step raw-byte CPU budget
  EXPORT_PART_UPLOAD_CAP:          z.number().int().positive(),   // per-step Drive upload (subrequest) cap
  EXPORT_MAX_BATCHES_PER_INSTANCE: z.number().int().positive(),   // continuation chaining bound
  EXPORT_RETENTION_DAYS:           z.number().int().positive(),   // Drive bundle/shard retention (days)

  // ── PAX plan-board export (1) ── (migration 0131, cf#53, 2026-08-24)
  // export-worker plan-board producer knob. The KV capture buffer
  // (pax-plan-board-capture) holds the local push-board.ps1 leg's point-in-time
  // snapshots; the daily PlanBoardExportChain prunes plan-board/** keys older
  // than this floor AFTER they roll into a published Drive bundle. 14 >>
  // EXPORT_DAILY_WINDOW_DAYS (1) so a skipped daily run self-heals (the next widened
  // window still finds the keys). Consumed ONLY by export-worker (loadWorkerParams).
  EXPORT_PB_CAPTURE_RETENTION_DAYS: z.number().int().positive(),  // plan-board KV capture-buffer retention (days)

  // ── Crypto Rank IC probe (1) ── (migration 0067, 2026-06-25)
  // RANK_PROBE_BASKETS — JSON array of TICKER_CLUSTERS keys the cross-sectional
  // rank-IC calibration probe forecasts each brief (one Flash call per listed
  // basket). Default ["crypto"]. Empty [] = probe disabled (kill-switch — hence
  // NO .min(1)). Each entry must be a key in TICKER_CLUSTERS (superRefine below).
  // Consumed by qa-worker (fires the probe); lives in the shared strict schema so
  // every loadRuntimeParams worker requires the row (deploy-then-migrate, 0067).
  // See docs/cross-sectional-rank-ic-2026-06-25-brief-revision.md §3 A14.
  RANK_PROBE_BASKETS:              z.array(z.string().min(1)),
});

// ─── Cross-field invariants (shared by the full schema AND every per-worker subset) ──────
// #50: each check guards on the PRESENCE of both keys it references, so a per-worker .pick()
// subset that omits one side simply skips that check. The full schema declares every key, so it
// runs them all — behaviour IDENTICAL to the old inline superRefine. Typed on Partial<ParamShape>
// because a subset is missing keys by construction (full RuntimeParams is assignable to it too).
type ParamShape = z.infer<typeof PARAM_SHAPE>;
// Explicit `| undefined` on every optional (not plain `Partial<>`, whose props are `?: T`): under
// `exactOptionalPropertyTypes` a `.partial()` schema's refine arg is `?: T | undefined`, so
// ParamsAdminSchema (partial+strict) needs this shape. The full ParamsSchema (strict, all-required)
// passes an all-present object, which is assignable to it too.
type ParamShapePartial = { [K in keyof ParamShape]?: ParamShape[K] | undefined };
function applyParamInvariants(p: ParamShapePartial, ctx: z.RefinementCtx): void {
  // Every RANK_PROBE_BASKETS entry must name an existing TICKER_CLUSTERS key — otherwise the
  // probe would fire over an undefined basket. Empty list is fine (disabled).
  if (p.RANK_PROBE_BASKETS && p.TICKER_CLUSTERS) {
    for (const basket of p.RANK_PROBE_BASKETS) {
      if (!(basket in p.TICKER_CLUSTERS)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom, path: ['RANK_PROBE_BASKETS'],
          message: `RANK_PROBE_BASKETS entry "${basket}" is not a key in TICKER_CLUSTERS`,
        });
      }
    }
  }
  // Ordering invariants for the retention windows (a bad Params edit that inverts these would
  // make a purge a no-op or delete still-needed rows).
  if (p.ACCOUNT_MAX_HISTORY_DAYS !== undefined && p.ACCOUNT_RAW_RETENTION_DAYS !== undefined
      && p.ACCOUNT_MAX_HISTORY_DAYS <= p.ACCOUNT_RAW_RETENTION_DAYS) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom, path: ['ACCOUNT_MAX_HISTORY_DAYS'],
      message: 'ACCOUNT_MAX_HISTORY_DAYS must exceed ACCOUNT_RAW_RETENTION_DAYS',
    });
  }
  // (Ops Notices warn/crit orderings are enforced by NOTIFICATION_THRESHOLDS' own nested
  // superRefine, not here.)
  if (p.SNAPSHOT_RETENTION_DAYS !== undefined && p.RAW_BLOB_STRIP_DAYS !== undefined
      && p.SNAPSHOT_RETENTION_DAYS <= p.RAW_BLOB_STRIP_DAYS) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom, path: ['SNAPSHOT_RETENTION_DAYS'],
      message: 'SNAPSHOT_RETENTION_DAYS must exceed RAW_BLOB_STRIP_DAYS',
    });
  }
  if (p.EXPORT_RETENTION_DAYS !== undefined && p.EXPORT_DAILY_WINDOW_DAYS !== undefined
      && p.EXPORT_RETENTION_DAYS <= p.EXPORT_DAILY_WINDOW_DAYS) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom, path: ['EXPORT_RETENTION_DAYS'],
      message: 'EXPORT_RETENTION_DAYS must exceed EXPORT_DAILY_WINDOW_DAYS (Drive must outlive one window so the consumer can ingest)',
    });
  }
}

/** The FULL runtime_params schema — all keys, strict, every cross-field invariant. Used for the
 *  seed/migration validation + the api-worker Params CRUD (which sees the whole table). Per-worker
 *  loaders use workerParamsSchema()/loadWorkerParams() so a param change only touches the workers
 *  that declare it (#50). */
export const ParamsSchema = PARAM_SHAPE.strict().superRefine(applyParamInvariants);

export type RuntimeParams = z.infer<typeof ParamsSchema>;

/** Admin-CRUD validation schema (#50, 2026-07-27) — PARTIAL + strict, same cross-field invariants.
 *  Used by the api-worker Params PATCH validation (validating one operator edit against the whole
 *  merged table). Differs from ParamsSchema in ONE way — `.partial()` — which is exactly what makes
 *  param RETIREMENT need no redeploy:
 *    · `.partial()`  → a key with NO row (a param retired by simply DELETING its D1 row) is tolerated,
 *                      so admin edits keep validating without first `.optional()`-ing + redeploying.
 *    · `.strict()`   → a genuinely UNKNOWN/rogue D1 key still fail-louds (the no-fallbacks drift guard
 *                      is preserved — retirement is a deliberate row-delete, a rogue key is a bug).
 *    · superRefine   → every cross-field invariant is guarded (`X !== undefined && Y !== undefined`),
 *                      so it self-skips absent keys and validates the co-present ones unchanged.
 *  The edited key is always present in the merged object, so its value + all relevant cross-field
 *  invariants are fully checked. Removing a retired key from PARAM_SHAPE becomes optional deferred
 *  cleanup (nothing breaks while it lingers rowless). Seed/migration validation still uses the full
 *  strict ParamsSchema (day-0 requires every key). */
export const ParamsAdminSchema = PARAM_SHAPE.partial().strict().superRefine(applyParamInvariants);

/** A strict schema over just `keys` (a subset of the full param set), reusing the shared cross-field
 *  invariants (which self-skip any check whose other key isn't in `keys`). #50: this is the
 *  per-worker schema — it still fail-louds on a wrong type or a key the worker didn't declare, but is
 *  BLIND to (and unaffected by) keys other workers own, which is what removes the fleet coupling. */
export function workerParamsSchema<K extends keyof RuntimeParams>(keys: readonly K[]) {
  const mask: Record<string, true> = {};
  for (const k of keys) mask[k as string] = true;
  // Zod's .pick() wants an Exactly<> mask type that a dynamically-built object trips; the mask is
  // runtime-correct (K ⊆ the shape keys by the constraint), so cast at this Zod-generics boundary and
  // restore the K-typing on the result so .parse() infers Pick<RuntimeParams, K>.
  const picked = PARAM_SHAPE.pick(mask as never) as z.ZodObject<Pick<typeof PARAM_SHAPE.shape, K>>;
  return picked.strict().superRefine(applyParamInvariants);
}

// ─── AMM snapshot retention ──────────────────────────────────────────────────
// #44 (2026-07-17) decoupled amm_snapshots from SNAPSHOT_RETENTION_DAYS (which dropped to
// shrink refresh_snapshots) so the crypto-tab LP-PnL + pool-ratio history kept its longer
// window. It lived as an in-code const (90) to skip the fleet fixture-lockstep tax.
// #43 (2026-08-19) PROMOTES it to the runtime_param AMM_SNAPSHOT_RETENTION_DAYS (PARAM_SHAPE
// above), seeded at 45 (migration 0127) for the D1 500MB capacity fit — it is now operator-
// tunable with no deploy. Consumers: chain-workflow retention purge (amm_snapshots +
// amm_liquidity_snapshots DELETE) and api-worker /api/amm positions-history ?days clamp, both
// via loadWorkerParams. There is no longer an in-code const — read the param.

// ─── Effective universe (CONTEXT ∪ TARGET) ──────────────────────────────────
// 2026-07-02 (docs/target-context-union-2026-07-02-brief-revision.md) — the
// priced/context/allowlist universe is CONTEXT_TICKERS with any TARGET_TICKERS
// not already present appended. This makes TARGET a subset of the universe BY
// CONSTRUCTION, so an operator adds a signal ticker by editing TARGET_TICKERS
// alone — it is automatically priced (refresh + daily-close), trusted by the
// macro-predictions writer, and rendered — without also having to remember to
// add it to CONTEXT_TICKERS. CONTEXT_TICKERS now means "context-only extras":
// tickers priced + fed as cross-asset context but NOT signal-emitting.
//
// Order-preserving + deduped: when TARGET ⊆ CONTEXT (the historical invariant),
// the result is CONTEXT_TICKERS verbatim — so this is a no-op for any config
// that already satisfied the old superset rule. The union only diverges when a
// TARGET ticker lives outside CONTEXT.
//
// EVERY "what is the universe" read routes through this (C026 completeness):
// refresh-worker pricing, chain fire, daily-close fetch list + its invariant,
// the macro-predictions `seed:` allowlist, qa fresh-context extraction, and the
// calibration aggregator ordering. Do not read params.CONTEXT_TICKERS directly
// as "the universe" — that reintroduces the two-list footgun this closes.
// #50: typed on just the two keys it reads (not the full RuntimeParams) so a
// per-worker loadWorkerParams() subset that declares only CONTEXT_TICKERS +
// TARGET_TICKERS can call it. Full RuntimeParams is still assignable, so every
// existing full-params caller is unaffected — this is a pure type widening.
export function effectiveUniverse(params: Pick<RuntimeParams, 'CONTEXT_TICKERS' | 'TARGET_TICKERS'>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const t of [...params.CONTEXT_TICKERS, ...params.TARGET_TICKERS]) {
    if (seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

// ─── D1 surface (structural; mirrors invariants/types.ts pattern) ───────────
// Declared here so consumers (apps/web etc.) do NOT need @cloudflare/workers-types
// as a transitive dep. The real D1Database from Workers types is structurally
// compatible — env.DB passes through without cast.

export interface RuntimeParamsD1 {
  prepare(query: string): RuntimeParamsD1Stmt;
}
export interface RuntimeParamsD1Stmt {
  all<T = unknown>(): Promise<{ results: T[]; success: boolean }>;
}

// ─── Type column → JS value decoder ─────────────────────────────────────────

export type RuntimeParamsRowType = 'string' | 'number' | 'boolean' | 'string_array' | 'json';

export function decodeValue(v: string, t: string): unknown {
  switch (t) {
    case 'string':       return v;
    case 'number':       return Number(v);
    case 'boolean':      return v === 'true';
    case 'string_array': return JSON.parse(v);
    case 'json':         return JSON.parse(v);
    default: throw new Error(`runtime_params: unknown type "${t}"`);
  }
}

// ─── Cache + loader ─────────────────────────────────────────────────────────

const TTL_MS = 60_000;

// Per key-set cache for loadWorkerParams (#50) — keyed by the sorted key list.
const workerCache = new Map<string, { params: unknown; expiresAt: number }>();

/** Test-only: drop the per-key-set cache so subsequent loadWorkerParams() refetch from D1. */
export function _resetRuntimeParamsCacheForTest(): void {
  workerCache.clear();
}

// (loadRuntimeParams — the full-strict all-keys loader — was REMOVED 2026-07-27 once #50's
// per-worker loadWorkerParams() migration reached its endpoint: its last caller, the
// daily-close DC-01 invariant, moved to loadWorkerParams(['CONTEXT_TICKERS','TARGET_TICKERS']).
// No worker reads the whole param set at runtime anymore; the only full-schema consumer left is
// the api-worker Params CRUD, which validates edits via ParamsAdminSchema (partial+strict).)

/** Load ONLY the params a worker declares, validated against its own strict subset schema. The
 *  worker passes its key list; tsc enforces completeness (reading params.X where X ∉ keys is a
 *  compile error, so a missing key can't slip to runtime). Rows for keys the worker doesn't declare
 *  are ignored — that is what removes the fleet fixture/redeploy tax: a param change only touches the
 *  workers that declare it (#50). Cached per key-set (same TTL as the full loader). */
export async function loadWorkerParams<K extends keyof RuntimeParams>(
  db: RuntimeParamsD1,
  keys: readonly K[],
): Promise<Pick<RuntimeParams, K>> {
  const cacheKey = [...keys].sort().join(',');
  const hit = workerCache.get(cacheKey);
  if (hit && hit.expiresAt > Date.now()) return hit.params as Pick<RuntimeParams, K>;

  const rows = await db
    .prepare('SELECT key, value, type FROM runtime_params')
    .all<{ key: string; value: string; type: string }>();
  if (!rows.success) throw new Error('runtime_params: D1 read returned success=false');
  if (rows.results.length === 0) throw new Error('runtime_params: zero rows — migration 0017 not applied?');

  const wanted = new Set<string>(keys as readonly string[]);
  const raw: Record<string, unknown> = {};
  for (const r of rows.results) {
    if (wanted.has(r.key)) raw[r.key] = decodeValue(r.value, r.type);
  }
  // Strict subset parse — fail-loud on a missing declared key or a wrong-typed value; keys this
  // worker did NOT declare were never selected, so they can't trip the .strict() (that is the point).
  const params = workerParamsSchema(keys).parse(raw) as Pick<RuntimeParams, K>;
  workerCache.set(cacheKey, { params, expiresAt: Date.now() + TTL_MS });
  return params;
}
